<template>
  <div class="text-center">
    <v-dialog
      v-model="dialog"
      class="change-primary-phone"
      width="1000px"
      scrollable
      persistent
      :loading="loadingPreviousAttempts"
    >
      <template #activator="{ on, attrs }">
        <v-btn
          v-if="hasPermission('create ticket::change-primary-email')"
          v-bind="attrs"
          color="primary"
          x-large
          block
          class="change-primary-phone-dialog-btn"
          v-on="on"
        >
          New Primary Phone
        </v-btn>
        <disabled-for-lack-of-role-btn
          v-else
          text="New Primary Phone"
          permission="create ticket::change-primary-email"
          :x-small="false"
          :x-large="true"
          :block="true"
          button-class="change-primary-phone-dialog-btn"
        />
      </template>

      <v-card
        v-if="starterQuestion"
        :loading="loadingModel || loadingPreviousAttempts"
      >
        <v-card-title class="text-h5 grey lighten-2">
          New Primary Phone
        </v-card-title>

        <v-card-text
          v-if="!loadingPreviousAttempts"
          class="py-5"
        >
          <v-row
            v-if="needFix"
            class="justify-center"
          >
            <v-col
              v-if="previousAttemptsStatus === 'REVERSE_COMPLETED'"
              class="justify-center"
            >
              <v-alert
                prominent
                dense
                dark
                text
                border="start"
                color="primary"
                icon="mdi-account-voice"
              >
                <blockquote class="blockquote">
                  There is a previous failed attempt to update the phone for this user.
                  That attempt had to be reverted, and for this reason,
                  it is not possible to process this request.
                </blockquote>
              </v-alert>
            </v-col>

            <v-col class="justify-center">
              <v-alert
                prominent
                dense
                dark
                text
                border="start"
                color="primary"
                icon="mdi-account-voice"
              >
                <blockquote class="blockquote">
                  Looks like you have a previous request to update your primary email that need
                  attention. Please complete the previous attempts before proceeding with a
                  new request.
                </blockquote>
              </v-alert>
            </v-col>
          </v-row>

          <v-row
            v-else
            class="justify-center"
          >
            <v-col class="justify-center">
              <v-alert
                prominent
                dense
                dark
                text
                border="start"
                color="primary"
                icon="mdi-account-voice"
              >
                <blockquote class="blockquote">
                  "Are you requesting this change PRIOR to reapplying for
                  more funding OR signing NEW loan docs?"
                </blockquote>
              </v-alert>
              <span class="text-caption">
                * If so, this request will be <b>DENIED</b> until <b>AFTER</b> the new loan
                documents are signed; this includes any manual loan docs sent out of LP.
              </span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />

        <v-card-actions class="py-3">
          <v-spacer />
          <v-btn
            v-if="!needFix && !loadingPreviousAttempts"
            color="primary"
            :loading="loadingModel"
            @click="starterQuestion = false"
          >
            No
          </v-btn>
          <v-btn
            v-if="!needFix && !loadingPreviousAttempts"
            color="secondary"
            text
            @click="dialog = false"
          >
            Yes
          </v-btn>
          <v-btn
            v-if="needFix"
            color="secondary"
            text
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card
        v-else
        :loading="loadingModel"
      >
        <v-card-title class="text-h5 grey lighten-2">
          New Primary Phone
        </v-card-title>

        <v-card-text class="py-5">
          <v-alert
            v-if="generalError"
            type="error"
            dismissible
            class="mb-4"
            @input="generalError = null"
          >
            {{ generalError }}
          </v-alert>
          <v-row class="justify-center">
            <v-col class="justify-center">
              <p>Current Phone Number</p>
            </v-col>
            <v-col>
              <v-text-field
                :value="displayCurrentPhone"
                label="Current Phone Number"
                outlined
                placeholder="(555)555-5555"
                disabled
              />
            </v-col>
          </v-row>

          <v-row class="justify-center">
            <v-col class="justify-center">
              <p>New Phone Number</p>
            </v-col>
            <v-col>
              <v-text-field
                v-model="newPhone"
                label="New Phone Number"
                outlined
                type="tel"
                placeholder="(555)555-5555"
                :rules="phoneRules"
                validate-on-blur
                @blur="normalizePhoneOnBlur"
                @input="onNewPhoneChange"
              />
            </v-col>
          </v-row>

          <v-row
            v-for="(destination, index) in destinations"
            :key="destination.key || 'destination-' + index"
            class="justify-center"
          >
            <v-col class="justify-center">
              <p>
                Change primary phone number in {{ destination.name }}?
                <br>
                <span class="text-caption"> * {{ destination.details }} </span>
              </p>
            </v-col>
            <v-col>
              <v-radio-group
                v-model="destinations[index].update"
                row
                disabled
              >
                <v-radio
                  :value="true"
                  :color="destination.update ? 'success' : 'error'"
                >
                  <template #label>
                    <strong
                      :class="destination.errorMessage
                        ? 'error--text'
                        : destination.update
                          ? 'success--text'
                          : ''"
                    >
                      {{ destination.errorMessage ? 'Try again' : 'Update' }}
                    </strong>
                  </template>
                </v-radio>
                <v-radio
                  :value="false"
                  color="success"
                >
                  <template #label>
                    <strong :class="!destination.update ? 'success--text' : ''">
                      {{ destination.reversed ? 'Reversed' : 'Updated' }}
                    </strong>
                  </template>
                </v-radio>
              </v-radio-group>

              <div v-if="destination.errorMessage">
                <span class="error--text">{{ destination.errorMessage }}</span>
              </div>
            </v-col>
          </v-row>

          <p>
            NOTE: We will place an internal note on the ticket once it's
            been updated and will send the following external macro AFTER
            the update has been completed: Primary Phone Updated
          </p>
          <v-row>
            <v-col>
              <v-textarea
                v-model="callNote"
                outlined
                label="Call Notes"
                placeholder="Additional information gathered during call (Optional)"
                rows="3"
                no-resize
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions class="py-3">
          <v-spacer />
          <v-btn
            color="primary"
            :loading="loadingModel"
            :disabled="loadingModel || !isPhoneValid"
            @click="processForm"
          >
            Change Phone
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="loadingModel"
            @click="
              starterQuestion = true;
              dialog = false;
            "
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <update-primary-phone-result-dialog
      v-model="showResultDialog"
      :completed="completed"
      :fails="fails"
      :result-alert="resultAlert"
      :destinations="destinations"
      :previous-status="previousAttemptsStatus"
      :ticket="ticket"
      :customer="customer"
      @close-parent-dialog="handleCloseParentDialog"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { datadogRum } from '@datadog/browser-rum';
import UpdatePrimaryPhoneResultDialog from '@/views/Tickets/ChangePrimaryPhone/UpdatePrimaryPhoneResultDialog.vue';
import DisabledForLackOfRoleBtn from '../../DisabledForLackOfRoleBtn.vue';

export default {
  name: 'NewPrimaryPhoneDialog',
  components: { UpdatePrimaryPhoneResultDialog, DisabledForLackOfRoleBtn },
  props: {
    ticket: { type: Object, required: true },
    customer: { type: Object, required: true },
  },
  data() {
    return {
      dialog: false,
      callNote: '',
      loadingModel: false,
      newPhone: null,
      destinations: [],
      starterQuestion: true,
      updateSuccess: null,
      completed: [],
      fails: [],
      showResultDialog: false,
      firstRequestSent: false,
      newPhoneErrors: [],
      generalError: null,
      loadingPreviousAttempts: false,
      resultAlert: '',
      previousAttemptsStatus: null,
      needFix: false,
      datadogRumSessionId: '0.0.0',
      phoneRules: [
        (v) => !!v || 'Phone number is required',
        (v) => {
          const digits = this.onlyDigits(v || '');
          const isValid = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
          return isValid || 'Please enter a valid phone number';
        },
        (v) => {
          const currentPhone = this.formatPhoneNoSpace(this.resolveCurrentPhone(this.customer));
          const newPhone = this.formatPhoneNoSpace(this.onlyDigits(v || ''));
          return newPhone !== currentPhone || 'New phone must be different from current phone';
        },
      ],
    };
  },
  computed: {
    displayCurrentPhone() {
      return this.resolveCurrentPhone(this.customer) || '';
    },
    isPhoneValid() {
      if (!this.newPhone) {
        return false;
      }

      const digits = this.onlyDigits(this.newPhone || '');
      const isValidFormat = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));

      const currentPhone = this.formatPhoneNoSpace(this.resolveCurrentPhone(this.customer));
      const newPhone = this.formatPhoneNoSpace(digits);
      const isDifferent = newPhone !== currentPhone;

      return isValidFormat && isDifferent;
    },
  },
  watch: {
    async dialog(newVal) {
      if (newVal) {
        this.clearAllErrors();
        await this.checkPreviousAttempts();
      }

      if (!newVal) {
        this.showResultDialog = false;
        this.clearFormData();
      }
    },
  },
  async created() {
    await this.getUpdateDestinations();
    if (datadogRum.getInternalContext() !== undefined) {
      this.datadogRumSessionId = datadogRum.getInternalContext().session_id || '0.0.0';
    }
  },
  methods: {
    ...mapActions('notification', ['setNotification']),
    ...mapActions('ticket', ['getTicket']),

    clearFormData() {
      this.newPhone = null;
      this.callNote = '';
      this.generalError = null;
      this.newPhoneErrors = [];
      this.completed = [];
      this.fails = [];
      this.resultAlert = '';
      this.previousAttemptsStatus = null;
      this.needFix = false;
      this.firstRequestSent = false;
      this.updateSuccess = null;
    },

    async checkPreviousAttempts() {
      this.loadingPreviousAttempts = true;
      try {
        const url = `api/v1/support/ticket/borrower/${this.customer.id * 1}/previous-attempts`;
        const payload = {
          ticketId: this.ticket.crm.id * 1 || null,
          updateType: 'phone',
        };

        const res = await this.$axios.post(url, payload);

        if (res.status === 200 && res.data) {
          const { data } = res;
          this.needFix = (data.pendingFix || data.previousAttempts?.status === 'REVERSE_COMPLETED') || false;
          this.previousAttemptsStatus = data.previousAttempts?.status || null;
          if (data.needAttentions) {
            this.completed = data.previousAttempts?.metadata?.final_result?.completed || [];
            this.fails = data.previousAttempts?.metadata?.final_result?.fails || [];
            this.resultAlert = `This customer has previously attempted to change their primary
                                email address that still needs attention. Please complete the
                                previous attempts before proceeding with a new request.`;

            if (this.previousAttemptsStatus === 'REVERSE_FAILED') {
              this.resultAlert = 'Previous reverse attempt failed. Manual intervention is required.';
            }

            this.showResultDialog = true;
          }
        }
      } catch (error) {
        let errMsg = 'There was an error processing your request';
        if (error.response && error.response.status === 404 && error.response.data) {
          errMsg = error.response.data.message;
        }
        this.setNotification({
          message: errMsg,
          color: 'red',
        });
      } finally {
        this.loadingPreviousAttempts = false;
      }
    },

    handleCloseParentDialog() {
      this.starterQuestion = true;
      this.firstRequestSent = false;
      this.completed = [];
      this.fails = [];
      this.clearAllErrors();
      this.clearFormData();
      this.dialog = false;
      this.showResultDialog = false;
    },

    onNewPhoneChange() {
      if (this.newPhoneErrors.length > 0) {
        this.newPhoneErrors = [];
      }
      if (this.generalError) {
        this.generalError = null;
      }
    },

    clearAllErrors() {
      this.newPhoneErrors = [];
      this.generalError = null;
    },

    setFormErrors(errors, generalMessage = null) {
      this.clearAllErrors();

      if (generalMessage) {
        this.formErrors.general = generalMessage;
      }

      if (errors && typeof errors === 'object') {
        Object.keys(errors).forEach((field) => {
          // eslint-disable-next-line no-prototype-builtins
          if (this.formErrors.hasOwnProperty(field)) {
            const fieldErrors = errors[field];
            this.formErrors[field] = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];
          }
        });
      }
    },

    async processForm() {
      this.clearAllErrors();

      const digits = this.onlyDigits(this.newPhone || '');
      if (!digits || (digits.length !== 10 && !(digits.length === 11 && digits.startsWith('1')))) {
        this.newPhoneErrors = ['Please enter a valid phone number'];
        this.generalError = 'There are some errors in the form. Please correct them before submitting.';
        return;
      }

      const currentPhone = this.formatPhoneNoSpace(this.resolveCurrentPhone(this.customer));
      const newPhone = this.formatPhoneNoSpace(digits);
      if (newPhone === currentPhone) {
        this.newPhoneErrors = ['New phone must be different from current phone'];
        this.generalError = 'There are some errors in the form. Please correct them before submitting.';
        return;
      }

      await this.sendRequest();
    },

    onlyDigits(v) {
      return String(v || '').replace(/\D+/g, '');
    },
    resolveCurrentPhone(customer) {
      if (!customer) return '';
      return customer.homephone;
    },
    formatPhoneNoSpace(v) {
      const d = this.onlyDigits(v || '');
      const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
      if (ten.length !== 10) return v || '';
      return `(${ten.slice(0, 3)})${ten.slice(3, 6)}-${ten.slice(6)}`;
    },
    normalizePhoneOnBlur() {
      const digits = this.onlyDigits(this.newPhone || '');
      if (!digits) return;
      const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
      if (ten.length === 10) {
        this.newPhone = this.formatPhoneNoSpace(ten);
      }
    },

    mergeRunResultIntoDestinations(resultData) {
      if (!resultData) return this.destinations;

      const byKey = resultData && typeof resultData === 'object' && !Array.isArray(resultData) ? resultData : {};
      const byName = {};
      Object.keys(resultData || {}).forEach((k) => {
        const v = (resultData || {})[k] || {};
        const name = v.name || k;
        byName[name] = { ...v };
      });

      return this.destinations.map((d) => {
        const incoming = (d.key && byKey[d.key]) || byName[d.name];
        if (!incoming) return d;

        let reversed = null;
        if (Object.prototype.hasOwnProperty.call(incoming, 'reversed')) {
          reversed = incoming.reversed;
        } else if (Object.prototype.hasOwnProperty.call(d, 'reversed')) {
          reversed = d.reversed;
        }

        return {
          ...d,
          ...incoming,
          reversed,
          errorMessage: Object.prototype.hasOwnProperty.call(incoming, 'errorMessage')
            ? incoming.errorMessage
            : d.errorMessage || null,
          rollbackError: Object.prototype.hasOwnProperty.call(incoming, 'rollbackError')
            ? incoming.rollbackError
            : d.rollbackError || null,
        };
      });
    },

    async sendRequest() {
      this.loadingModel = true;

      const digits = this.onlyDigits(this.newPhone || '');
      if (!digits || (digits.length !== 10 && !(digits.length === 11 && digits.startsWith('1')))) {
        this.setNotification({ message: 'Please enter a valid phone number', color: 'red' });
        this.loadingModel = false;
        return;
      }

      const normalizedTen = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

      const currentFormatted = this.formatPhoneNoSpace(this.resolveCurrentPhone(this.customer));
      const newFormatted = this.formatPhoneNoSpace(normalizedTen);

      const url = `api/v1/support/ticket/borrower/${this.customer.id * 1}/update-primary-phone`;
      const payload = {
        ticketId: this.ticket.crm.id * 1,
        currentPhone: currentFormatted,
        newPhone: newFormatted,
        callNote: this.callNote,
        destinations: this.destinations,
        huiSessionId: this.datadogRumSessionId,
      };

      try {
        const res = await this.$axios.post(url, payload);

        if (res.status === 200 && res.data) {
          if (res.data.success) {
            await this.getTicket(this.$route.params.id);
          } else {
            this.destinations = this.mergeRunResultIntoDestinations(res.data.data);
            this.updateSuccess = res.data.success;

            const msg = res.data.message
              || 'There was an error updating one or more systems. Automatic rollback was executed. Manual intervention may be required.';
            this.setNotification({ message: msg, color: 'red' });
          }

          this.completed = res.data.completed || [];
          this.fails = res.data.fails || [];
          this.showResultDialog = true;
        } else {
          this.setNotification({
            message: 'The request returned successful, but please check the platforms for confirmation that it worked.',
            color: 'green',
          });
        }
      } catch (error) {
        this.firstRequestSent = true;
        const resp = error && error.response ? error.response : null;
        const data = resp && resp.data ? resp.data : null;

        if (resp && resp.status === 409 && data) {
          this.completed = data.completed || [];
          this.fails = data.fails || [];
          this.showResultDialog = true;
        }

        if (resp && resp.status === 422 && data) {
          if (data.errors && data.errors.newPhone) {
            this.newPhoneErrors = Array.isArray(data.errors.newPhone)
              ? data.errors.newPhone
              : [data.errors.newPhone];
          }

          if (data.message) {
            this.generalError = data.message;
          }

          if (data.data) {
            this.destinations = this.mergeRunResultIntoDestinations(data.data);
          }
          this.updateSuccess = data.success ?? false;
        } else if (data && typeof data === 'object') {
          if (data.data) {
            this.destinations = this.mergeRunResultIntoDestinations(data.data);
          }
          this.updateSuccess = data.success ?? false;
        } else {
          let errMsg = 'There was an error processing your request';
          if (resp && typeof resp.data === 'string') {
            errMsg = resp.data || errMsg;
          }
          this.generalError = errMsg;
        }
      } finally {
        this.loadingModel = false;
      }
    },

    async getUpdateDestinations() {
      this.loadingModel = true;
      try {
        const res = await this.$axios.get('api/v1/support/ticket/borrower/update-phone-destinations');

        if (res.status === 200 && res.data) {
          const raw = res.data;

          const preferredOrder = ['loanpro', 'customers', 'crm'];

          const list = Array.isArray(raw)
            ? raw
            : preferredOrder
              .filter((k) => Object.prototype.hasOwnProperty.call(raw, k))
              .map((k) => ({ key: k, ...(raw[k] || {}) }))
              .concat(
                Object.keys(raw)
                  .filter((k) => preferredOrder.indexOf(k) === -1)
                  .map((k) => ({ key: k, ...(raw[k] || {}) })),
              );

          this.destinations = list.map((d) => ({
            ...d,
            name: d.name || (d.key ? d.key : ''),
            details: d.details || '',
            update: typeof d.update === 'boolean' ? d.update : Boolean(d.update),
            reversed: Object.prototype.hasOwnProperty.call(d, 'reversed') ? d.reversed : null,
            errorMessage: d.errorMessage || null,
            rollbackError: d.rollbackError || null,
          }));
        } else {
          this.destinations = [];
        }
      } catch (error) {
        let errMsg = 'There was an error trying to retrieve update destinations. Please reload this TBot Action';
        if (error.response && error.response.status === 422 && error.response.data) {
          errMsg = error.response.data.message;
        }
        this.setNotification({ message: errMsg, color: 'red' });
      } finally {
        this.loadingModel = false;
      }
    },
  },
};
</script>
